import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Star, Send, Ticket } from "lucide-react";
import { toast } from "sonner";

export default function CRM() {
    const { activeHotelId } = useAuth();
    const [feedback, setFeedback] = useState([]);
    const [summary, setSummary] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [openCamp, setOpenCamp] = useState(false);
    const [openCoup, setOpenCoup] = useState(false);
    const [cForm, setCForm] = useState({ name: "", channel: "email", subject: "", body: "", target_segment: "all" });
    const [cpForm, setCpForm] = useState({ code: "", discount_type: "percent", discount_value: 10, valid_till: "", max_uses: 100, description: "" });

    const load = async () => {
        if (!activeHotelId) return;
        try {
            const [f, s, c, cp] = await Promise.all([
                api.get(`/crm/feedback?hotel_id=${activeHotelId}`),
                api.get(`/crm/feedback/summary?hotel_id=${activeHotelId}`),
                api.get(`/crm/campaigns?hotel_id=${activeHotelId}`).catch(() => ({ data: [] })),
                api.get(`/crm/coupons?hotel_id=${activeHotelId}`),
            ]);
            setFeedback(f.data); setSummary(s.data); setCampaigns(c.data); setCoupons(cp.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const submitCamp = async () => {
        try {
            await api.post("/crm/campaigns", { ...cForm, hotel_id: activeHotelId });
            toast.success("Campaign sent (MOCKED delivery)");
            setOpenCamp(false); setCForm({ name: "", channel: "email", subject: "", body: "", target_segment: "all" }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const submitCoup = async () => {
        try {
            await api.post("/crm/coupons", { ...cpForm, hotel_id: activeHotelId, discount_value: Number(cpForm.discount_value), max_uses: Number(cpForm.max_uses) });
            toast.success("Coupon created"); setOpenCoup(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="CRM" subtitle={`${feedback.length} feedback · ${campaigns.length} campaigns · ${coupons.length} coupons`}>
            {summary && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Avg Rating</div>
                        <div className="flex items-baseline gap-2 mt-2">
                            <div className="text-3xl font-display font-bold text-slate-900">{summary.avg_rating}</div>
                            <div className="flex text-[#C5B08A]">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < Math.round(summary.avg_rating) ? "fill-current" : ""}`} />)}</div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{summary.count} reviews</div>
                    </Card>
                    {[5, 4, 3].map(r => (
                        <Card key={r} className="p-5 border border-slate-200 rounded-md shadow-none">
                            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{r} stars</div>
                            <div className="text-3xl font-display font-bold text-slate-900 mt-2">{summary.by_rating?.[r] || 0}</div>
                        </Card>
                    ))}
                </div>
            )}
            <Tabs defaultValue="feedback">
                <TabsList>
                    <TabsTrigger value="feedback">Feedback</TabsTrigger>
                    <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
                    <TabsTrigger value="coupons">Coupons</TabsTrigger>
                </TabsList>

                <TabsContent value="feedback" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {feedback.map(f => (
                            <Card key={f.id} className="p-4 border border-slate-200 rounded-md shadow-none">
                                <div className="flex justify-between">
                                    <div className="font-semibold">{f.guest_name}</div>
                                    <div className="flex text-[#C5B08A]">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < f.rating ? "fill-current" : ""}`} />)}</div>
                                </div>
                                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{f.category}</div>
                                <div className="text-sm mt-2 text-slate-700">{f.comment}</div>
                            </Card>
                        ))}
                        {feedback.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">No feedback yet</div>}
                    </div>
                </TabsContent>

                <TabsContent value="campaigns" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={openCamp} onOpenChange={setOpenCamp}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="camp-add-btn"><Send className="w-4 h-4 mr-1" /> Campaign</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>New Campaign</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div><Label className="text-xs uppercase tracking-wider">Name</Label><Input value={cForm.name} onChange={e => setCForm({ ...cForm, name: e.target.value })} data-testid="camp-name" /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Channel</Label>
                                        <Select value={cForm.channel} onValueChange={v => setCForm({ ...cForm, channel: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>{["email", "sms", "whatsapp"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Segment</Label>
                                        <Select value={cForm.target_segment} onValueChange={v => setCForm({ ...cForm, target_segment: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>{[["all", "All guests"], ["loyalty", "Loyalty members"], ["recent", "Recent guests"], ["birthday", "Birthday this month"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    {cForm.channel === "email" && <div><Label className="text-xs uppercase tracking-wider">Subject</Label><Input value={cForm.subject} onChange={e => setCForm({ ...cForm, subject: e.target.value })} /></div>}
                                    <div><Label className="text-xs uppercase tracking-wider">Message</Label><Textarea rows={4} value={cForm.body} onChange={e => setCForm({ ...cForm, body: e.target.value })} data-testid="camp-body" /></div>
                                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                        <strong>MOCKED delivery.</strong> Provide RESEND_API_KEY / Twilio keys to enable real delivery.
                                    </div>
                                </div>
                                <DialogFooter><Button onClick={submitCamp} disabled={!cForm.name || !cForm.body} className="bg-[#0A1C3E]" data-testid="camp-submit">Send</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Channel</th><th className="px-4 py-3">Segment</th><th className="px-4 py-3">Recipients</th><th className="px-4 py-3">Sent</th><th className="px-4 py-3">Status</th></tr>
                            </thead>
                            <tbody>
                                {campaigns.map(c => (
                                    <tr key={c.id} className="border-b border-slate-100">
                                        <td className="px-4 py-3 text-sm font-medium">{c.name}</td>
                                        <td className="px-4 py-3 text-xs uppercase tracking-wider">{c.channel}</td>
                                        <td className="px-4 py-3 text-xs">{c.target_segment}</td>
                                        <td className="px-4 py-3 text-sm font-mono">{c.recipient_count}</td>
                                        <td className="px-4 py-3 text-xs">{c.sent_at?.slice(0, 10)}</td>
                                        <td className="px-4 py-3"><span className="status-badge bg-amber-100 text-amber-800 border-amber-200">{c.mocked ? "MOCKED" : "delivered"}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="coupons" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={openCoup} onOpenChange={setOpenCoup}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="coup-add-btn"><Ticket className="w-4 h-4 mr-1" /> Coupon</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>New Coupon</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div><Label className="text-xs uppercase tracking-wider">Code</Label><Input value={cpForm.code} onChange={e => setCpForm({ ...cpForm, code: e.target.value.toUpperCase() })} placeholder="WELCOME10" data-testid="coup-code" /></div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label className="text-xs uppercase tracking-wider">Type</Label>
                                            <Select value={cpForm.discount_type} onValueChange={v => setCpForm({ ...cpForm, discount_type: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
                                            </Select></div>
                                        <div><Label className="text-xs uppercase tracking-wider">Value</Label><Input type="number" value={cpForm.discount_value} onChange={e => setCpForm({ ...cpForm, discount_value: e.target.value })} /></div>
                                    </div>
                                    <div><Label className="text-xs uppercase tracking-wider">Valid Till</Label><Input type="date" value={cpForm.valid_till} onChange={e => setCpForm({ ...cpForm, valid_till: e.target.value })} /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Max Uses</Label><Input type="number" value={cpForm.max_uses} onChange={e => setCpForm({ ...cpForm, max_uses: e.target.value })} /></div>
                                </div>
                                <DialogFooter><Button onClick={submitCoup} disabled={!cpForm.code} className="bg-[#0A1C3E]" data-testid="coup-submit">Create</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {coupons.map(c => (
                            <Card key={c.id} className="p-4 border-2 border-dashed border-[#C5B08A] rounded-md bg-[#FDFCF9]">
                                <div className="flex justify-between">
                                    <div className="font-mono text-lg font-bold text-[#0A1C3E]">{c.code}</div>
                                    <Ticket className="w-4 h-4 text-[#C5B08A]" />
                                </div>
                                <div className="mt-2 text-xl font-display font-bold">
                                    {c.discount_type === "percent" ? `${c.discount_value}% OFF` : `₹${c.discount_value} OFF`}
                                </div>
                                <div className="text-xs text-slate-500 mt-2">Valid till {c.valid_till} · {c.used}/{c.max_uses} used</div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}
